import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Star, X, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Daily Glimmer Prompt — once-a-day nudge on Dashboard to log a Glimmer.
 * Hidden if user already logged today (per /api/echo-map/void-status hours_since_last_glimmer < 24).
 * Dismissible per day (stored in localStorage with date key).
 */

// Rotating gentle prompts — different each day
const GLIMMER_PROMPTS = [
  "Where did things go right today, even in a small way?",
  "What small piece of evidence showed up that you're on the right path?",
  "What landed differently than your old pattern would have?",
  "Where did you receive something you used to have to chase?",
  "What synchronicity, ease, or small yes did you notice today?",
  "Where did life rearrange itself in your favor?",
  "What did your energy attract today — even quietly?",
  "What's whispering 'it's working,' even if it's not shouting yet?",
];

const DISMISS_KEY = 'glimmer_prompt_dismissed_date';

const todayKey = () => new Date().toISOString().slice(0, 10); // YYYY-MM-DD

const promptForToday = () => {
  // Deterministic prompt-of-the-day
  const day = Math.floor(Date.now() / (1000 * 60 * 60 * 24));
  return GLIMMER_PROMPTS[day % GLIMMER_PROMPTS.length];
};

export default function DailyGlimmerPrompt() {
  const navigate = useNavigate();
  const [shouldShow, setShouldShow] = useState(false);
  const [event, setEvent] = useState('');
  const [score, setScore] = useState(7);
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const check = async () => {
      // Dismissed today?
      if (localStorage.getItem(DISMISS_KEY) === todayKey()) {
        setShouldShow(false);
        return;
      }
      try {
        const res = await api.get('/echo-map/void-status');
        const { hours_since_last_glimmer, total_glimmers } = res.data;
        // Show if: never logged, or last glimmer was >= 24h ago
        if (total_glimmers === 0 || (hours_since_last_glimmer ?? Infinity) >= 24) {
          setShouldShow(true);
        }
      } catch {
        // silent fail — just don't show
      }
    };
    check();
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, todayKey());
    setShouldShow(false);
  };

  const submit = async () => {
    if (!event.trim()) return;
    setSubmitting(true);
    try {
      await api.post('/echo-map/glimmers', {
        glimmer_event: event.trim(),
        certainty_score: score,
      });
      localStorage.setItem(DISMISS_KEY, todayKey());
      setShouldShow(false);
      toast.success('Added to your map');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not log sign');
    } finally {
      setSubmitting(false);
    }
  };

  if (!shouldShow) return null;

  return (
    <div
      className="relative rounded-2xl overflow-hidden animate-fade-up"
      data-testid="daily-glimmer-prompt"
      style={{
        background: 'linear-gradient(135deg, #FBF7EE 0%, rgba(198,178,143,0.15) 100%)',
        border: '1px solid rgba(198,178,143,0.35)',
        boxShadow: '0 8px 32px rgba(198,178,143,0.12)',
      }}
    >
      {/* Tiny stars */}
      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 12 }).map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-gold"
            style={{
              left: `${(i * 43) % 100}%`,
              top: `${(i * 31) % 100}%`,
              width: 2, height: 2,
              opacity: 0.3 + ((i * 7) % 5) / 10,
              animation: `breathe ${3 + (i % 3)}s ease-in-out ${i * 0.2}s infinite alternate`,
            }}
          />
        ))}
      </div>

      <button
        onClick={dismiss}
        data-testid="daily-glimmer-dismiss"
        className="absolute top-4 right-4 z-10 text-text-muted hover:text-dark p-1"
        style={{ transition: 'color 0.2s ease' }}
        aria-label="Dismiss"
      >
        <X size={16} />
      </button>

      <div className="relative p-7 sm:p-8">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-10 h-10 rounded-full bg-gold/15 flex items-center justify-center shrink-0">
            <Star size={18} className="text-gold" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-gold font-sans text-[10px] tracking-[0.35em] uppercase mb-1">Today's Sign</p>
            <p className="font-serif text-xl sm:text-2xl text-dark leading-snug">
              {promptForToday()}
            </p>
          </div>
        </div>

        {!expanded ? (
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-end">
            <Button
              variant="ghost"
              onClick={() => navigate('/echo-map')}
              data-testid="daily-glimmer-open-map"
              className="text-text-muted hover:text-dark font-sans text-xs tracking-wider uppercase"
            >
              Open your Signs <ArrowRight size={12} className="ml-1" />
            </Button>
            <Button
              onClick={() => setExpanded(true)}
              data-testid="daily-glimmer-quick-log"
              className="bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-xs tracking-[0.2em] uppercase btn-hover h-11 px-5"
            >
              Log a Sign
            </Button>
          </div>
        ) : (
          <div className="space-y-4 mt-2">
            <Textarea
              value={event}
              onChange={(e) => setEvent(e.target.value)}
              data-testid="daily-glimmer-event-input"
              placeholder="A stranger said something you needed to hear. An opportunity arrived easily. You caught yourself responding from your new self..."
              rows={3}
              autoFocus
              className="bg-bone/70 border-black/8 focus:border-gold focus:ring-gold/30 rounded-xl font-sans leading-relaxed resize-none"
            />
            <div className="flex items-center gap-3">
              <span className="text-text-muted text-[10px] tracking-[0.2em] uppercase font-sans whitespace-nowrap">Confidence</span>
              <Input
                type="range"
                min={1}
                max={10}
                value={score}
                onChange={(e) => setScore(parseInt(e.target.value))}
                data-testid="daily-glimmer-score"
                className="flex-1 accent-[#C6B28F]"
              />
              <span className="font-serif text-xl text-dark w-8 text-center" data-testid="daily-glimmer-score-display">{score}</span>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => { setExpanded(false); setEvent(''); }}
                data-testid="daily-glimmer-cancel"
                className="font-sans text-xs text-text-muted h-10"
              >
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={!event.trim() || submitting}
                data-testid="daily-glimmer-submit"
                className="bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-xs tracking-[0.2em] uppercase btn-hover h-10 px-5"
              >
                {submitting ? 'Adding...' : 'Add to your Map'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
